/**
 * Admin panel: moderate flagged / inappropriate family tree comments.
 * Toggle `hidden` on family_comments docs.
 */
import { useEffect, useState } from "react";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { Eye, EyeOff, MessageSquare } from "lucide-react";

export default function AdminFamilyComments() {
  const [items, setItems] = useState([]);
  const [includeHidden, setIncludeHidden] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const { data } = await http.get("/admin/family-comments", { params: { include_hidden: includeHidden } });
      setItems(data.items);
    } catch (e) { toast.error("Failed to load comments"); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [includeHidden]);

  const toggle = async (cid, hidden) => {
    try {
      await http.patch(`/admin/family-comments/${cid}`, { hidden });
      toast.success(hidden ? "Comment hidden" : "Comment restored");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div data-testid="admin-family-comments">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="overline mb-1">FAMILY COMMENTS</div>
          <h2 className="font-display text-2xl">Moderate community memories</h2>
          <p className="text-sm mt-1" style={{ color: "var(--wofy-text-soft)" }}>
            Hide any comment that breaches Social Pulse community standards. Hidden comments
            remain in the database but are not shown on public trees.
          </p>
        </div>
        <label className="text-xs inline-flex items-center gap-2">
          <input type="checkbox" checked={includeHidden} onChange={(e) => setIncludeHidden(e.target.checked)} data-testid="afc-include-hidden" />
          Show hidden
        </label>
      </div>
      {busy && items.length === 0 && <p className="text-sm">Loading…</p>}
      {items.length === 0 && !busy && (
        <div className="wofy-card text-sm text-center" style={{ color: "var(--wofy-text-soft)" }}>No comments yet.</div>
      )}
      <div className="space-y-2">
        {items.map((c) => (
          <div key={c.id} className="wofy-card flex items-start gap-3" data-testid={`afc-item-${c.id}`}>
            <MessageSquare className="w-4 h-4 mt-1 shrink-0" style={{ color: "var(--wofy-primary)" }} />
            <div className="flex-1 min-w-0">
              <div className="text-xs" style={{ color: "var(--wofy-text-soft)" }}>
                {c.author_name} · {new Date(c.created_at).toLocaleString()} · Tree {c.tree_id.slice(0, 8)} · Member {c.member_id.slice(0, 8)}
                {c.hidden && <span className="ml-2 text-[10px] uppercase font-mono tracking-wider" style={{ color: "#B23A3A" }}>HIDDEN</span>}
              </div>
              <p className="mt-1 text-sm whitespace-pre-wrap break-words">{c.body}</p>
            </div>
            {c.hidden ? (
              <button onClick={() => toggle(c.id, false)} data-testid={`afc-restore-${c.id}`} className="wofy-btn-ghost text-xs">
                <Eye className="w-3.5 h-3.5" /> Restore
              </button>
            ) : (
              <button onClick={() => toggle(c.id, true)} data-testid={`afc-hide-${c.id}`} className="wofy-btn-ghost text-xs">
                <EyeOff className="w-3.5 h-3.5" /> Hide
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
